import { FileMagnifyingGlass, Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import {
  Asset,
  CustomerWithDriverOnboarding,
} from '../../../../../types/apiTypes';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import AssetList from './widgets/customer-assets/AssetList';
import MaintenanceReminders from './widgets/customer-assets/MaintenanceReminders';
import PendingTruckApprovals from './widgets/pending-trucks/PendingTruckApprovals';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../../../utils/ScreenViewContext';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isPreActiveCustomer } from '../home/utils/demoData';
import { useMemo } from 'react';
import { useDemo } from '../../../../../utils/DemoContext';

interface AssetsHomeProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  selectedAsset: Asset | null;
  setSelectedAsset: (asset: Asset | null) => void;
  onAssetChange: () => void;
}

function AssetsHome({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  selectedAsset,
  setSelectedAsset,
  onAssetChange,
}: AssetsHomeProps) {
  const navigate = useNavigate();
  const { screenView } = useScreenView();
  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const { userRecord } = useUser();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  const { isDemo } = useDemo();

  // Query keys
  const assetsKey = ['assets', userId ?? ''];
  const remindersKey = ['maintenanceReminders', 'customer', userId ?? ''];

  // Fetch functions
  const fetchCustomerAssets = async () => {
    const response = await callProtectedApi(
      '/assets/' + (userId ?? ''),
      'get',
      {}
    );
    return response?.data?.data?.assets || [];
  };

  const fetchMaintenanceReminders = async () => {
    const response = await callProtectedApi(
      '/maintenanceReminders/customer/' + (userId ?? ''),
      'get',
      {}
    );
    return response?.data?.data?.maintenance_reminders || [];
  };

  // Demo assets data. Each truck carries an `insurance_payment_amount` so the
  // fleet-insurance summary line adds up to a real figure in a demo session
  // rather than reading "$0.00/mo total" over six trucks (M7, John 2026-08-25).
  const demoAssets: Asset[] = isDemo
    ? ([
        {
          asset_type: 'truck',
          truck_id: 'demo-truck-1',
          insurance_payment_amount: '425.00',
          truck_class_number: '8',
          truck_class: 'Heavy Duty',
          truck_display_name: 'Unit 101',
          vehicle_id_number: '1XPWD40X1ED215307',
          truck_make: 'peterbilt',
          truck_model: '579',
          truck_year: '2023',
          ownership_status: 'owned',
        },
        {
          asset_type: 'truck',
          truck_id: 'demo-truck-2',
          insurance_payment_amount: '510.00',
          truck_class_number: '8',
          truck_class: 'Heavy Duty',
          truck_display_name: 'Unit 102',
          vehicle_id_number: '3AKJHHDR5NSMP7124',
          truck_make: 'freightliner',
          truck_model: 'cascadia',
          truck_year: '2022',
          ownership_status: 'leaseToOwn',
        },
        {
          asset_type: 'truck',
          truck_id: 'demo-truck-3',
          insurance_payment_amount: '480.00',
          truck_class_number: '8',
          truck_class: 'Heavy Duty',
          truck_display_name: 'Unit 103',
          vehicle_id_number: '1HTMSTAR4MH603842',
          truck_make: 'international',
          truck_model: 'lt',
          truck_year: '2024',
          ownership_status: 'owned',
        },
        {
          asset_type: 'truck',
          truck_id: 'demo-truck-4',
          insurance_payment_amount: '365.00',
          truck_class_number: '7',
          truck_class: 'Medium Duty',
          truck_display_name: 'Unit 104',
          vehicle_id_number: '2NKHHJ7X0KM948561',
          truck_make: 'kenworth',
          truck_model: 't680',
          truck_year: '2021',
          ownership_status: 'leased',
        },
        {
          asset_type: 'truck',
          truck_id: 'demo-truck-5',
          insurance_payment_amount: '455.00',
          truck_class_number: '8',
          truck_class: 'Heavy Duty',
          truck_display_name: 'Unit 105',
          vehicle_id_number: '4V4NC9EH5LN257893',
          truck_make: 'volvo',
          truck_model: 'vnl 860',
          truck_year: '2023',
          ownership_status: 'owned',
        },
        {
          asset_type: 'truck',
          truck_id: 'demo-truck-6',
          insurance_payment_amount: '440.00',
          truck_class_number: '8',
          truck_class: 'Heavy Duty',
          truck_display_name: 'Unit 106',
          vehicle_id_number: '1M1AN07Y59N006742',
          truck_make: 'mack',
          truck_model: 'anthem',
          truck_year: '2022',
          ownership_status: 'owned',
        },
      ] as unknown as Asset[])
    : [];

  // Queries
  const { data: carrierAssetsData = demoAssets, isLoading: loadingAssets } =
    useQuery({
      queryKey: assetsKey,
      queryFn: fetchCustomerAssets,
      enabled: !!(userId ?? '') && !isDemo,
    });

  const {
    data: maintenanceRemindersData = [],
    isLoading: loadingMaintenanceReminders,
  } = useQuery({
    queryKey: remindersKey,
    queryFn: fetchMaintenanceReminders,
    enabled: !!(userId ?? '') && !isPreActive,
  });

  // Function to invalidate queries (pass this down to child components)
  const refreshData = {
    assets: () => queryClient.invalidateQueries({ queryKey: assetsKey }),
    reminders: () => queryClient.invalidateQueries({ queryKey: remindersKey }),
    all: () => {
      queryClient.invalidateQueries({ queryKey: assetsKey });
      queryClient.invalidateQueries({ queryKey: remindersKey });
    },
  };

  const updateSelectedAsset = (asset: Asset) => {
    setSelectedAsset(asset);
  };

  const heightTable = useMemo(() => {
    if (maintenanceRemindersData?.length) {
      return { assetHeight: '50%', reminderHeight: '50%' };
    }
    return { assetHeight: '70%', reminderHeight: '30%' };
  }, [maintenanceRemindersData?.length]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="flex h-full w-[1148px] max-w-[1148px] items-center justify-center overflow-y-auto overflow-x-hidden pt-4">
            {maintenanceRemindersData?.length > 0 ||
            carrierAssetsData?.length > 0 ? (
              <div className="flex h-full w-full flex-col space-y-[16px] py-[16px]">
                <PendingTruckApprovals />
                <div
                  className={`flex h-[${heightTable.assetHeight}] min-h-[${heightTable.assetHeight}] w-full flex-row`}
                >
                  <AssetList
                    carrierAssetsData={carrierAssetsData}
                    setSelectedAsset={updateSelectedAsset}
                    loadingAssets={loadingAssets}
                    onAssetChange={refreshData.assets}
                    isPreActive={isPreActive}
                  />
                </div>
                <div
                  className={`flex h-[${heightTable.reminderHeight}] min-h-[${heightTable.reminderHeight}] w-full flex-row`}
                >
                  <MaintenanceReminders
                    maintenanceRemindersData={maintenanceRemindersData}
                    loadingReminders={loadingMaintenanceReminders}
                    assetList={carrierAssetsData}
                    onRecordChange={refreshData.reminders}
                    isPreActive={isPreActive}
                  />
                </div>
              </div>
            ) : !loadingAssets && !loadingMaintenanceReminders ? (
              <div className="flex h-full w-full flex-col justify-center space-y-[16px] bg-white py-[16px]">
                <div className="flex flex-col items-center justify-center gap-y-2 align-middle">
                  <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-3">
                    <FileMagnifyingGlass
                      size={40}
                      className="text-graysecondary"
                    />
                  </div>
                  <div className="flex flex-col items-center justify-center gap-y-1 align-middle">
                    <h2 className="text-[16px] text-primary">
                      No Assets to Display
                    </h2>
                    <p className="text-[14px] text-graysecondary">
                      Get started by adding your first asset
                    </p>
                  </div>
                  <AccentButtonOutline
                    onClick={() => navigate('/dashboard/assets/create')}
                    disabled={isDemo}
                    addClass="rounded-full text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Plus size={20} />
                    <h2 className="text-[14px]">Add Asset</h2>
                  </AccentButtonOutline>
                </div>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingAssets || loadingMaintenanceReminders}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
      case 'tablet':
        return (
          <div className="flex h-full w-[568px] max-w-[568px] items-center justify-center overflow-y-auto overflow-x-hidden">
            {maintenanceRemindersData?.length > 0 ||
            carrierAssetsData?.length > 0 ? (
              <div className="flex h-full w-full flex-col space-y-[16px] py-[16px] pb-[32px]">
                <PendingTruckApprovals />
                <div
                  className={`flex h-[${heightTable.assetHeight}] min-h-[${heightTable.assetHeight}] w-full flex-row`}
                >
                  <AssetList
                    carrierAssetsData={carrierAssetsData}
                    setSelectedAsset={updateSelectedAsset}
                    loadingAssets={loadingAssets}
                    onAssetChange={refreshData.assets}
                    isPreActive={isPreActive}
                  />
                </div>
                <div
                  className={`flex h-[${heightTable.reminderHeight}] min-h-[${heightTable.reminderHeight}] w-full flex-row`}
                >
                  <MaintenanceReminders
                    maintenanceRemindersData={maintenanceRemindersData}
                    loadingReminders={loadingMaintenanceReminders}
                    assetList={carrierAssetsData}
                    onRecordChange={refreshData.reminders}
                    isPreActive={isPreActive}
                  />
                </div>
              </div>
            ) : !loadingAssets && !loadingMaintenanceReminders ? (
              <div className="flex h-full w-full flex-col justify-center space-y-[16px] bg-white py-[16px] pb-[32px]">
                <div className="flex flex-col items-center justify-center gap-y-2 align-middle">
                  <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-3">
                    <FileMagnifyingGlass
                      size={40}
                      className="text-graysecondary"
                    />
                  </div>
                  <div className="flex flex-col items-center justify-center gap-y-1 align-middle">
                    <h2 className="text-[16px] text-primary">
                      No Assets to Display
                    </h2>
                    <p className="text-[14px] text-graysecondary">
                      Get started by adding your first asset
                    </p>
                  </div>
                  <AccentButtonOutline
                    onClick={() => navigate('/dashboard/assets/create')}
                    disabled={isDemo}
                    addClass="rounded-full text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Plus size={20} />
                    <h2 className="text-[14px]">Add Asset</h2>
                  </AccentButtonOutline>
                </div>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingAssets || loadingMaintenanceReminders}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
      case 'mobile':
        return (
          <div className="flex h-full w-[351px] max-w-[351px] items-center justify-center overflow-y-auto overflow-x-hidden">
            {maintenanceRemindersData?.length > 0 ||
            carrierAssetsData?.length > 0 ? (
              <div className="flex h-full w-full flex-col space-y-[16px] py-[16px] pb-[32px]">
                <PendingTruckApprovals />
                <div
                  className={`flex h-[${heightTable.assetHeight}] min-h-[${heightTable.assetHeight}] w-full flex-row`}
                >
                  <AssetList
                    carrierAssetsData={carrierAssetsData}
                    setSelectedAsset={updateSelectedAsset}
                    loadingAssets={loadingAssets}
                    onAssetChange={refreshData.assets}
                    isPreActive={isPreActive}
                  />
                </div>
                <div
                  className={`flex h-[${heightTable.reminderHeight}] min-h-[${heightTable.reminderHeight}] w-full flex-row`}
                >
                  <MaintenanceReminders
                    maintenanceRemindersData={maintenanceRemindersData}
                    loadingReminders={loadingMaintenanceReminders}
                    assetList={carrierAssetsData}
                    onRecordChange={refreshData.reminders}
                    isPreActive={isPreActive}
                  />
                </div>
              </div>
            ) : !loadingAssets && !loadingMaintenanceReminders ? (
              <div className="flex h-full w-full flex-col justify-center space-y-[16px] bg-white py-[16px] pb-[32px]">
                <div className="flex flex-col items-center justify-center gap-y-2 align-middle">
                  <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-3">
                    <FileMagnifyingGlass
                      size={40}
                      className="text-graysecondary"
                    />
                  </div>
                  <div className="flex flex-col items-center justify-center gap-y-1 align-middle">
                    <h2 className="text-[16px] text-primary">
                      No Assets to Display
                    </h2>
                    <p className="text-[14px] text-graysecondary">
                      Get started by adding your first asset
                    </p>
                  </div>
                  <AccentButtonOutline
                    onClick={() => navigate('/dashboard/assets/create')}
                    disabled={isDemo}
                    addClass="rounded-full text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Plus size={20} />
                    <h2 className="text-[14px]">Add Asset</h2>
                  </AccentButtonOutline>
                </div>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingAssets || loadingMaintenanceReminders}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default AssetsHome;
